import { authActions } from '../actionTypes';
import Auth0 from '../../auth/Auth0';
import createLogger from '../../utils/createLogger';

const DEBUG = false;
const debug = createLogger(DEBUG, `Auth0.js`);


/** ---------------- action creators ---------------- */
/* ---- auth0 authentication ---- */
const authRequest = () => ({
  type: authActions.AUTH_REQUEST
});
const authSuccess = (session) => ({
  type: authActions.AUTH_SUCCESS,
  session,
});
const authFailure = (error) => ({
  type: authActions.AUTH_FAILURE,
  error,
});

/* ---- auth0 user details retrieval ---- */
const userRequest = () => ({
  type: authActions.USER_REQUEST,
});
const userSuccess = (user) => ({
  type: authActions.USER_SUCCESS,
  user,
});
const userFailure = (error) => ({
  type: authActions.USER_FAILURE,
  error,
});

/* ---- clear auth state ---- */
const clearAuth = () => ({
  type: authActions.CLEAR_AUTH,
});


/** ---------------- action sequences ---------------- */
/**
 * Authenticates an existing user using Auth0,
 * then retrieves the authenticated user's data
 */
const login = ({ service, email, password }, callback) => {
  debug.enter(`login`);
  debug.log(email && `  using credentials:`, {email, password});
  debug.log(service && `  using service: ${service}`);

  return service
    ? () => Auth0.authService(service)
    : (dispatch) => {
      dispatch(authRequest());

      debug.log(`  calling Auth0.login`);
      return Auth0.login({username: email, password}, (err, authResult) => {
        if (err) {
          debug.log(`  > login error:`, err);
          dispatch(authFailure(err));
          return callback(err);
        }

        debug.log(`  > login success:`, authResult);
        dispatch(authSuccess());
        _getUser(callback)(dispatch);
      });
  };
};


/**
 * Registers & authenticates new user using Auth0,
 * then retrieves the authenticated user's data
 */
const register = ({ service, email, password }, callback) => {
  debug.enter(`register`);
  debug.log(email && `  using credentials:`, {email, password});
  debug.log(service && `  using service: ${service}`);

  return service
    ? () => Auth0.authService(service)
    : (dispatch) => {
      dispatch(authRequest());

      debug.log(`  calling Auth0.register`);
      return Auth0.register({email, password}, (err, res) => {
        if (err) {
          debug.log(`  > register error:`, err);
          dispatch(authFailure(err));
          return callback(err);
        }
        debug.log(`  > register success:`, res);
        dispatch(authSuccess(res));
        dispatch(_getUser(callback));
      });
    };
};


/**
 * Processes authentication response from external service
 * (passed by Auth0 as a callback URL with data in hash),
 * then retrieves the authenticated user's data
 */
const authCallback = (hash, callback) => {
  debug.enter(`authCallback`);
  debug.log(`  hash:`, hash);

  return (dispatch) => {
    dispatch(authRequest());

    debug.log(`  calling Auth0.parseHash`);
    if (/access_token|id_token|error/.test(hash)) {
      Auth0.serviceCallback(hash, (err, res) => {
        if (err) {
          debug.log(`    callback error:`, err);
          dispatch(authFailure(err, `callback`));
          return callback(err);
        }
        debug.log(`    callback success:`, res);
        dispatch(authSuccess(res));
        dispatch(_getUser(callback));
      });

    } else {
      debug.log(`    callback error: hash regex test was bad`);
      dispatch(authFailure(`callback triggered with invalid hash`));
    }
  }
};

/**
 * Logs user out by clearing the auth state in the
 * browser's session storage and the Redux store
 */
const logout = (callback) => {
  debug.enter(`logout`);
  return dispatch => {
    Auth0.clearSession();  // flush session storage
    dispatch(clearAuth()); // flush user from store
    callback();
  }
};


/** ---------------- private sequences ---------------- */
/*
 * Fetches user data for an authenticated user from Auth0
 * server TODO: Fetch internal user data from ae database
 */
const _getUser = (callback) => {
  return (dispatch) => {
    debug.enter(`getDetails`);
    dispatch(userRequest());

    return Auth0.getUserDetails((err, user) => {
      debug.log(`  > getDetails callback`);

      if (err) {
        dispatch(userFailure(err));
        debug.log(`    details error:`, err);

        if (err.type !== `noauth`) {
          debug.log(`    clearing session`);
          Auth0.clearSession();
        }
        callback(err);

      } else {
        debug.log(`    details success:`, user);
        dispatch(userSuccess(user));
        callback();
      }
    });
  }
};


export {
  login,
  logout,
  register,
  authCallback,
}



// WEBPACK FOOTER //
// ./src/data/auth/authActions.js